import { editsOf, findSection, value, values } from '../../parser/fortigate'
import type { FgConfig, FgEdit } from '../../parser/types'
import { finding, type Finding, type Rule } from '../types'

/**
 * VPN crypto and exposure.
 *
 * A tunnel negotiated with a broken cipher is worse than no tunnel, because everyone
 * downstream believes the traffic is protected. These are also the settings most likely
 * to be inherited unchanged from a decade-old template.
 */

/** Encryption and hash tokens that should not appear in any proposal in 2026. */
const WEAK_PROPOSAL_TOKENS = ['des', '3des', 'md5', 'sha1', 'null']

/** Diffie-Hellman groups whose modulus is too small to resist a well-funded attacker. */
const WEAK_DH_GROUPS = ['1', '2', '5']

function weakTokensIn(proposals: string[]): string[] {
  const found = new Set<string>()
  for (const proposal of proposals) {
    for (const part of proposal.toLowerCase().split('-')) {
      // `des` must not match inside `3des`, and `sha1` must not match `sha128`.
      if (WEAK_PROPOSAL_TOKENS.includes(part)) found.add(part)
    }
  }
  return [...found]
}

/** Phase 1 entries across both the interface-mode and policy-mode sections. */
function phase1Entries(config: FgConfig): FgEdit[] {
  return [
    ...editsOf(config, 'vpn', 'ipsec', 'phase1-interface'),
    ...editsOf(config, 'vpn', 'ipsec', 'phase1'),
  ]
}

function phase2Entries(config: FgConfig): FgEdit[] {
  return [
    ...editsOf(config, 'vpn', 'ipsec', 'phase2-interface'),
    ...editsOf(config, 'vpn', 'ipsec', 'phase2'),
  ]
}

export const weakIpsecPhase1: Rule = {
  id: 'fg-ipsec-weak-phase1',
  title: 'IPsec phase 1 proposal uses broken cryptography',
  run(config) {
    const out: Finding[] = []

    for (const p1 of phase1Entries(config)) {
      const weak = weakTokensIn(values(p1, 'proposal'))
      if (weak.length === 0) continue

      out.push(
        finding(weakIpsecPhase1, {
          severity: 'high',
          title: `VPN "${p1.key}" negotiates phase 1 with ${weak.join(', ')}`,
          detail:
            `The phase 1 proposal for ${p1.key} offers ${values(p1, 'proposal').join(', ')}. ` +
            'DES and 3DES have inadequate effective key strength, and MD5 and SHA-1 are broken for ' +
            'collision resistance. A peer that prefers the weak option gets it, so offering it at all ' +
            'is enough to make the tunnel weak.',
          location: `vpn ipsec phase1 ${p1.key}`,
          line: p1.line,
          pci: '2.2.7',
          remediation:
            `config vpn ipsec phase1-interface\n    edit "${p1.key}"\n` +
            '        set proposal aes256gcm-prfsha384 aes256-sha256\n    next\nend',
        }),
      )
    }

    return out
  },
}

export const weakIpsecPhase2: Rule = {
  id: 'fg-ipsec-weak-phase2',
  title: 'IPsec phase 2 proposal uses broken cryptography',
  run(config) {
    const out: Finding[] = []

    for (const p2 of phase2Entries(config)) {
      const weak = weakTokensIn(values(p2, 'proposal'))
      if (weak.length === 0) continue

      out.push(
        finding(weakIpsecPhase2, {
          severity: 'high',
          title: `VPN "${p2.key}" encrypts payload with ${weak.join(', ')}`,
          detail:
            `The phase 2 proposal for ${p2.key} offers ${values(p2, 'proposal').join(', ')}. ` +
            'This is the cipher that protects the actual traffic, not just the key exchange, so a ' +
            'weak choice here exposes everything the tunnel carries.',
          location: `vpn ipsec phase2 ${p2.key}`,
          line: p2.line,
          pci: '2.2.7',
          remediation:
            `config vpn ipsec phase2-interface\n    edit "${p2.key}"\n` +
            '        set proposal aes256gcm aes256-sha256\n    next\nend',
        }),
      )
    }

    return out
  },
}

export const weakDiffieHellmanGroup: Rule = {
  id: 'fg-ipsec-weak-dhgrp',
  title: 'IPsec key exchange uses a small Diffie-Hellman group',
  run(config) {
    const out: Finding[] = []

    for (const entry of [...phase1Entries(config), ...phase2Entries(config)]) {
      const groups = values(entry, 'dhgrp')
      const weak = groups.filter((g) => WEAK_DH_GROUPS.includes(g))
      if (weak.length === 0) continue

      out.push(
        finding(weakDiffieHellmanGroup, {
          severity: 'medium',
          title: `VPN "${entry.key}" offers Diffie-Hellman group ${weak.join(', ')}`,
          detail:
            `Key exchange for ${entry.key} accepts group ${weak.join(', ')}. Groups 1, 2 and 5 use ` +
            'moduli small enough that precomputation against a widely reused prime makes recorded ' +
            'traffic decryptable later.',
          location: `vpn ipsec ${entry.key}`,
          line: entry.line,
          pci: '2.2.7',
          remediation:
            `config vpn ipsec phase1-interface\n    edit "${entry.key}"\n` +
            '        set dhgrp 21 20 14\n    next\nend',
        }),
      )
    }

    return out
  },
}

export const aggressiveModeIpsec: Rule = {
  id: 'fg-ipsec-aggressive-mode',
  title: 'IPsec phase 1 in aggressive mode',
  run(config) {
    return phase1Entries(config)
      .filter((p1) => value(p1, 'mode') === 'aggressive')
      .map((p1) =>
        finding(aggressiveModeIpsec, {
          severity: 'medium',
          title: `VPN "${p1.key}" uses aggressive mode`,
          detail:
            `Phase 1 for ${p1.key} is set to aggressive mode, which sends the identity and a hash of ` +
            'the pre-shared key before the channel is encrypted. Anyone who can capture the exchange ' +
            'can attack the pre-shared key offline, at their leisure.',
          location: `vpn ipsec phase1 ${p1.key}`,
          line: p1.line,
          pci: '2.2.7',
          remediation:
            `config vpn ipsec phase1-interface\n    edit "${p1.key}"\n` +
            '        set mode main\n    next\nend\n' +
            '# Main mode requires a fixed peer address or certificate authentication.',
        }),
      )
  },
}

export const sslVpnWeakTls: Rule = {
  id: 'fg-sslvpn-weak-tls',
  title: 'SSL-VPN accepts deprecated TLS versions',
  run(config) {
    const settings = findSection(config, 'vpn', 'ssl', 'settings')
    if (settings === undefined || value(settings, 'status') === 'disable') return []

    const minVersion = value(settings, 'ssl-min-proto-ver')
    if (minVersion === undefined || ['tls1-2', 'tls1-3'].includes(minVersion)) return []

    return [
      finding(sslVpnWeakTls, {
        severity: 'high',
        title: `SSL-VPN accepts ${minVersion} and above`,
        detail:
          `vpn ssl settings has ssl-min-proto-ver set to ${minVersion}. The SSL-VPN portal is by ` +
          'definition reachable from the internet, so accepting a deprecated TLS version there ' +
          'exposes remote-access credentials to downgrade attacks.',
        location: 'vpn ssl settings',
        line: settings.line,
        pci: '2.2.7',
        remediation: 'config vpn ssl settings\n    set ssl-min-proto-ver tls1-2\nend',
      }),
    ]
  },
}

export const sslVpnWebMode: Rule = {
  id: 'fg-sslvpn-web-mode',
  title: 'SSL-VPN web mode enabled',
  run(config) {
    const settings = findSection(config, 'vpn', 'ssl', 'settings')
    if (settings === undefined || value(settings, 'status') === 'disable') return []

    const portals = editsOf(config, 'vpn', 'ssl', 'web', 'portal').filter(
      (p) => value(p, 'web-mode') === 'enable',
    )
    if (portals.length === 0) return []

    return [
      finding(sslVpnWebMode, {
        severity: 'medium',
        title: `SSL-VPN web mode is enabled on ${portals.map((p) => `"${p.key}"`).join(', ')}`,
        detail:
          'Web mode runs a clientless HTTP proxy inside the firewall itself. It is a large, ' +
          'internet-facing code path that has repeatedly been the subject of critical FortiOS ' +
          'advisories. Tunnel mode does the same job without that surface.',
        location: 'vpn ssl web portal',
        line: portals[0]?.line,
        remediation:
          `config vpn ssl web portal\n    edit "${portals[0]?.key ?? '<portal>'}"\n` +
          '        set web-mode disable\n        set tunnel-mode enable\n    next\nend',
      }),
    ]
  },
}

export const sslVpnFromAnySource: Rule = {
  id: 'fg-sslvpn-any-source',
  title: 'SSL-VPN reachable from any source address',
  run(config) {
    const settings = findSection(config, 'vpn', 'ssl', 'settings')
    if (settings === undefined || value(settings, 'status') === 'disable') return []

    const sources = values(settings, 'source-address').map((s) => s.toLowerCase())
    if (sources.length > 0 && !sources.includes('all')) return []

    return [
      finding(sslVpnFromAnySource, {
        severity: 'low',
        title: 'SSL-VPN accepts connections from any address on the internet',
        detail:
          'vpn ssl settings places no source-address restriction on the portal, so it answers every ' +
          'host on the internet. That is normal for a roaming workforce, but it means the login page ' +
          'absorbs continuous credential stuffing — restrict it by geography or by known ranges if ' +
          'your users allow it.',
        location: 'vpn ssl settings',
        line: settings.line,
        remediation:
          'config vpn ssl settings\n    set source-address "ALLOWED_COUNTRIES"\nend\n' +
          '# Or leave it open and rely on multi-factor authentication plus lockout.',
      }),
    ]
  },
}

export const VPN_RULES: Rule[] = [
  weakIpsecPhase1,
  weakIpsecPhase2,
  weakDiffieHellmanGroup,
  aggressiveModeIpsec,
  sslVpnWeakTls,
  sslVpnWebMode,
  sslVpnFromAnySource,
]
